/**
 * OpenInvoices — issue #25 (Becky-T1.2); server-of-record #140.
 *
 * End-of-day "Open Invoices" queue. Lists invoices Becky checked out via
 * "Settle Later" so they can be batch-processed or processed one-by-one.
 *
 * Storage is now the server-of-record (`adminPaymentsApi`). Reads/settles via
 * `/api/v2/admin/payments/open-invoices*`. The UX (select-all / batch / single
 * settle) is unchanged.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  adminPaymentsApi,
  type AdminDeskPaymentMethod,
  type AdminInvoiceAdjustmentInput,
  type AdminOpenInvoice,
} from '../../lib/api.js';
import { formatServiceDay } from '../../lib/outstanding.js';
// Ted #22 (#983) — the SAME Adjust-price form Settle Now uses. Ted dismissed
// Settle Now to look for the discount; this queue is where the invoice
// actually went, so the discount has to be reachable from here too.
import { InvoiceAdjustFields, type AdjustKind } from './InvoiceAdjustFields.js';
import './OpenInvoices.css';

// #812 — staff-pickable tenders for this queue. A SINGLE settle requires
// one (the server 400s a desk settle without a method); a BATCH sweep may
// run without one and records the rows as unrecorded tender (honest for
// mixed end-of-day sweeps).
const TENDER_OPTIONS: ReadonlyArray<{
  value: AdminDeskPaymentMethod;
  label: string;
}> = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'external_terminal', label: 'Card terminal' },
  { value: 'other', label: 'Other' },
];

export interface OpenInvoicesProps {
  /** Base API URL — same value `Payments.tsx` passes to the sibling queue. */
  readonly apiUrl: string;
  /** Optional refresh trigger — bump when external state changes. */
  readonly refreshKey?: number;
}

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatTimestamp(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export function OpenInvoices({
  apiUrl,
  refreshKey = 0,
}: OpenInvoicesProps): JSX.Element {
  const [invoices, setInvoices] = useState<AdminOpenInvoice[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bumpRefresh, setBumpRefresh] = useState(0);
  // #812 — the tender the next settle records ('' = none picked yet).
  const [tender, setTender] = useState<AdminDeskPaymentMethod | ''>('');
  // Ted #22 (#983) — the row whose Adjust form is open (null = none), and
  // its in-progress values. One at a time: the counter is not a spreadsheet.
  const [adjusting, setAdjusting] = useState<string | null>(null);
  const [adjustKind, setAdjustKind] = useState<AdjustKind>('percent');
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [adjustError, setAdjustError] = useState<string | null>(null);

  const closeAdjust = useCallback(() => {
    setAdjusting(null);
    setAdjustValue('');
    setAdjustReason('');
    setAdjustError(null);
    setAdjustSaving(false);
  }, []);

  const handleApplyAdjustment = useCallback(
    async (invoiceId: string) => {
      const reason = adjustReason.trim();
      const parsed = Number(adjustValue);
      if (!Number.isFinite(parsed) || reason.length < 2) return;
      // Percent is whole; amount and override are dollars on the wire's cents.
      const value = adjustKind === 'percent' ? Math.round(parsed) : Math.round(parsed * 100);
      const body: AdminInvoiceAdjustmentInput = { kind: adjustKind, value, reason };
      setAdjustSaving(true);
      setAdjustError(null);
      const res = await adminPaymentsApi.adjustOpenInvoice(apiUrl, invoiceId, body);
      setAdjustSaving(false);
      if (res.error || !res.data?.invoice) {
        setAdjustError(res.error ?? 'Could not record the adjustment.');
        return;
      }
      closeAdjust();
      setBumpRefresh((n) => n + 1);
    },
    [apiUrl, adjustKind, adjustValue, adjustReason, closeAdjust],
  );

  const reload = useCallback(async () => {
    setLoading(true);
    const res = await adminPaymentsApi.getOpenInvoices(apiUrl, {
      status: 'open',
    });
    setLoading(false);
    if (res.error || !res.data) {
      setError(res.error ?? 'Could not load open invoices.');
      return;
    }
    setError(null);
    // Defensive: tolerate a response missing the `invoices` array.
    setInvoices(Array.isArray(res.data.invoices) ? res.data.invoices : []);
    setSelected(new Set());
  }, [apiUrl]);

  useEffect(() => {
    void reload();
  }, [reload, refreshKey, bumpRefresh]);

  const allChecked =
    invoices.length > 0 && invoices.every((i) => selected.has(i.id));

  const toggleOne = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === invoices.length) return new Set();
      return new Set(invoices.map((i) => i.id));
    });
  }, [invoices]);

  const handleSettleOne = useCallback(
    async (id: string) => {
      // #812 — a single settle must say how it was paid (server 400s
      // without it); the button is disabled until a tender is picked,
      // this guard is belt-and-braces.
      if (!tender) {
        setError('Pick how it was paid (cash / check / card terminal) first.');
        return;
      }
      const res = await adminPaymentsApi.settleOpenInvoice(apiUrl, id, {
        paymentMethod: tender,
      });
      if (res.error) {
        setError(res.error);
        return;
      }
      setError(null);
      setBumpRefresh((n) => n + 1);
    },
    [apiUrl, tender],
  );

  const handleBatchSettle = useCallback(async () => {
    if (selected.size === 0) return;
    // #812 — a batch sweep without a tender records the rows as
    // UNRECORDED tender (the button says so), never a fabricated one.
    const res = await adminPaymentsApi.batchSettleOpenInvoices(
      apiUrl,
      Array.from(selected),
      tender || undefined,
    );
    if (res.error) {
      setError(res.error);
      return;
    }
    setError(null);
    setBumpRefresh((n) => n + 1);
  }, [apiUrl, selected, tender]);

  // #813 — settle one owner's whole cart into ONE payment (requires the
  // tender select, same as a single settle).
  const handleSettleCart = useCallback(
    async (rows: AdminOpenInvoice[]) => {
      if (!tender) {
        setError('Pick how it was paid (cash / check / card terminal) first.');
        return;
      }
      const expected = rows.reduce((sum, i) => sum + i.totalCents, 0);
      const res = await adminPaymentsApi.settleCart(
        apiUrl,
        rows.map((i) => i.id),
        tender,
        expected,
      );
      if (res.error) {
        setError(res.error);
        return;
      }
      setError(null);
      setBumpRefresh((n) => n + 1);
    },
    [apiUrl, tender],
  );

  const totalCents = useMemo(
    () => invoices.reduce((sum, i) => sum + i.totalCents, 0),
    [invoices],
  );

  // #813 — the queue groups by owner so a multi-service visit reads as one
  // cart. Grouping is presentational; per-row settle and the cross-customer
  // batch sweep are unchanged.
  const customerGroups = useMemo(() => {
    const groups = new Map<string, AdminOpenInvoice[]>();
    for (const inv of invoices) {
      const list = groups.get(inv.customerId) ?? [];
      list.push(inv);
      groups.set(inv.customerId, list);
    }
    return [...groups.values()];
  }, [invoices]);
  const selectedTotalCents = useMemo(
    () =>
      invoices
        .filter((i) => selected.has(i.id))
        .reduce((sum, i) => sum + i.totalCents, 0),
    [invoices, selected],
  );

  return (
    <article
      className="ferro-card ferro-open-invoices"
      aria-label="Open invoices queue"
      data-testid="open-invoices"
    >
      <header className="ferro-card__header ferro-open-invoices__header">
        <div>
          <h2 className="ferro-section__subtitle">Open Invoices</h2>
          <p className="ferro-muted ferro-open-invoices__count">
            {loading
              ? 'Loading open invoices…'
              : invoices.length === 0
                ? 'Nothing waiting to be settled.'
                : `${invoices.length} unsettled · ${formatMoney(totalCents)} total`}
          </p>
        </div>
        {invoices.length > 0 ? (
          <div className="ferro-open-invoices__batch">
            <label className="ferro-field ferro-open-invoices__tender">
              <span className="ferro-field__label">How paid</span>
              <select
                className="ferro-field__input"
                value={tender}
                onChange={(e) =>
                  setTender(e.target.value as AdminDeskPaymentMethod | '')
                }
                data-testid="open-invoices-tender"
              >
                <option value="">Not recorded</option>
                {TENDER_OPTIONS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="ferro-btn ferro-btn--ghost"
              onClick={toggleAll}
              data-testid="open-invoices-toggle-all"
            >
              {allChecked ? 'Clear selection' : 'Select all'}
            </button>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              disabled={selected.size === 0}
              onClick={() => void handleBatchSettle()}
              data-testid="open-invoices-batch-settle"
            >
              Batch settle
              {selected.size > 0
                ? ` (${selected.size} · ${formatMoney(selectedTotalCents)})`
                : ''}
              {tender === '' ? ' — tender unrecorded' : ''}
            </button>
          </div>
        ) : null}
      </header>

      {error ? (
        <p
          className="ferro-muted ferro-open-invoices__error"
          role="alert"
          data-testid="open-invoices-error"
        >
          {error}
        </p>
      ) : null}

      {loading || invoices.length === 0 ? null : (
        <div className="ferro-open-invoices__groups" aria-label="Open invoices">
          {customerGroups.map((rows) => (
            <section
              key={rows[0]!.customerId}
              className="ferro-open-invoices__group"
              data-testid={`open-invoices-group-${rows[0]!.customerId}`}
            >
              {rows.length > 1 ? (
                <div className="ferro-open-invoices__group-head">
                  <span className="ferro-open-invoices__group-name">
                    {rows[0]!.customerName || 'Customer'} — {rows.length} services ·{' '}
                    {formatMoney(rows.reduce((sum, i) => sum + i.totalCents, 0))}
                  </span>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    onClick={() => void handleSettleCart(rows)}
                    disabled={tender === ''}
                    title={
                      tender === ''
                        ? 'Pick how it was paid first (the dropdown above)'
                        : undefined
                    }
                    data-testid={`open-invoices-settle-cart-${rows[0]!.customerId}`}
                  >
                    Settle together
                  </button>
                </div>
              ) : null}
              <ul className="ferro-open-invoices__list">
                {rows.map((inv) => {
            const checked = selected.has(inv.id);
            return (
              <li
                key={inv.id}
                className={`ferro-open-invoices__row${
                  adjusting === inv.id ? ' ferro-open-invoices__row--adjusting' : ''
                }`}
                data-testid={`open-invoice-row-${inv.id}`}
              >
                <label className="ferro-open-invoices__select">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleOne(inv.id)}
                    aria-label={`Select invoice ${inv.id.slice(0, 8)}`}
                    data-testid={`open-invoice-select-${inv.id}`}
                  />
                </label>
                <div className="ferro-open-invoices__body">
                  <div className="ferro-open-invoices__primary">
                    {/* Ted #22 (#983) — the DOG leads the row. "$0.00 ·
                        #68a6ebc9" is an id Ted could not connect to Juniper;
                        a name is what the desk actually looked for. The
                        booking id stays, one size down, for the audit trail. */}
                    {inv.dogNames && inv.dogNames.length > 0 ? (
                      <span data-testid={`open-invoice-dogs-${inv.id}`}>
                        {inv.dogNames.join(', ')} —{' '}
                      </span>
                    ) : null}
                    {formatMoney(inv.totalCents)} · #
                    <code>{inv.bookingId.slice(0, 8)}</code>
                  </div>
                  <div className="ferro-open-invoices__meta">
                    {inv.customerName || 'Customer'} ·{' '}
                    {formatTimestamp(inv.createdAt)}
                    {inv.notes ? ` · ${inv.notes}` : ''}
                  </div>
                  {/* Ted #29 (#1108) — an added service's line says what it
                      is, which day, and who did it. */}
                  {inv.parentBookingId ? (
                    <div className="ferro-open-invoices__meta" data-testid={`open-invoice-leg-${inv.id}`}>
                      {inv.serviceLegLabel ?? inv.serviceTypeName}
                      {inv.serviceDate ? ` · ${formatServiceDay(inv.serviceDate.slice(0, 10))}` : ''}
                      {inv.performedBy ? ` · ${inv.performedBy}` : ' · nobody assigned'}
                    </div>
                  ) : null}
                  {/* Ted #3 + #9 (#906 #912) — the price change and its reason
                      travel with the invoice wherever it is listed. */}
                  {inv.adjustments && inv.adjustments.length > 0 ? (
                    <div
                      className="ferro-open-invoices__meta"
                      data-testid={`open-invoice-adjusted-${inv.id}`}
                    >
                      Was {formatMoney(inv.originalTotalCents ?? inv.adjustments[0]!.originalTotalCents)} ·{' '}
                      {inv.adjustments.map((a) => a.reason).join(' · ')}
                    </div>
                  ) : null}
                  {/* Ted #22 (#983) — Adjust price, in place, on the row
                      where the invoice actually is. The same form Settle Now
                      shows; only the parent's API call differs. */}
                  {adjusting === inv.id ? (
                    <InvoiceAdjustFields
                      idPrefix={`open-invoice-adjust-${inv.id}`}
                      variant="inline"
                      kind={adjustKind}
                      value={adjustValue}
                      reason={adjustReason}
                      saving={adjustSaving}
                      error={adjustError}
                      onKindChange={setAdjustKind}
                      onValueChange={setAdjustValue}
                      onReasonChange={setAdjustReason}
                      onApply={() => void handleApplyAdjustment(inv.id)}
                      onCancel={closeAdjust}
                    />
                  ) : null}
                </div>
                <div className="ferro-open-invoices__actions">
                  {adjusting === inv.id ? null : (
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      onClick={() => {
                        closeAdjust();
                        setAdjusting(inv.id);
                      }}
                      data-testid={`open-invoice-adjust-open-${inv.id}`}
                    >
                      Adjust price
                    </button>
                  )}
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    onClick={() => void handleSettleOne(inv.id)}
                    disabled={tender === ''}
                    title={
                      tender === ''
                        ? 'Pick how it was paid first (the dropdown above)'
                        : undefined
                    }
                    data-testid={`open-invoice-settle-${inv.id}`}
                  >
                    Settle
                  </button>
                </div>
                  </li>
                );
              })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </article>
  );
}
